import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../../../auth/auth-store.js";
import { sameSession, scopeFromClaims } from "../../../auth/session-scope.js";
import { useAuditExportDownload } from "../../../store/audit-export-download.js";
import { useDownloadGuard } from "./download-guard.js";
import {
  auditQueryKeys,
  getAuditExport,
  getAuditExportDownload,
  type AuditExportJob,
} from "../../../api/admin-audit-api.js";

/**
 * Downloads the export this session asked for, as soon as it is ready — and
 * renders nothing. It lives on Audit & compliance only: a download is a
 * top-level navigation, and on a page holding unsaved edits that would raise
 * the browser's own "Leave site?" dialog. Away from this page the bell
 * notification carries the result; coming back resumes the wait.
 *
 * Nothing here runs on a page load: with no watched job the query is disabled,
 * so no export request is sent and no export can reappear.
 */
export function AuditExportAutoDownload({
  tenantId,
}: {
  readonly tenantId: string | null | undefined;
}): null {
  const scope = scopeFromClaims(useAuthStore((s) => s.claims));
  const watchedScope = useAuditExportDownload((s) => s.scope);
  const watchedJobId = useAuditExportDownload((s) => s.jobId);
  const jobId =
    watchedJobId !== null && sameSession(watchedScope, scope)
      ? watchedJobId
      : null;

  const query = useQuery({
    queryKey: auditQueryKeys.export(tenantId, jobId ?? "none"),
    queryFn: () => getAuditExport(jobId as string),
    enabled: Boolean(tenantId) && jobId !== null,
    refetchInterval: (current) =>
      current.state.data && isInProgress(current.state.data) ? 2_000 : false,
  });
  const job = query.data;
  const unreadable = query.isError;
  const beginDownload = useDownloadGuard();

  useEffect(() => {
    if (jobId === null) return;
    if (!unreadable && (!job || job.jobId !== jobId || isInProgress(job))) {
      return;
    }
    /* Clear BEFORE anything else, and only if this job is still the watched
       one. That single check is what keeps it to one download: a re-render, a
       StrictMode re-run of this same effect, or a bell link that already
       claimed the job (AuditExportLinkHandler runs first) all find it gone. */
    if (!clearIfStillWatched(jobId)) return;
    /* Failed, expired or unreadable: nothing to download, and the bell
       notification says so. */
    if (unreadable || job?.status !== "ready") return;
    const stillCurrent = beginDownload();
    void getAuditExportDownload(jobId).then(
      /* Dropped if the page has gone or the session changed meanwhile; the
         bell still links the file. */
      (grant) => {
        if (stillCurrent()) window.location.assign(grant.url);
      },
      /* No toast: nothing on this flow floats. The bell still downloads it. */
      () => undefined,
    );
  }, [jobId, job, unreadable, beginDownload]);

  return null;
}

function clearIfStillWatched(jobId: string): boolean {
  const state = useAuditExportDownload.getState();
  if (state.jobId !== jobId) return false;
  state.clear();
  return true;
}

function isInProgress(job: AuditExportJob): boolean {
  return job.status === "queued" || job.status === "processing";
}
